import React, { useContext, useCallback, useEffect, useMemo } from 'react';
import { isEmailValid } from 'helpers/validation';
import { useTranslation } from 'react-i18next';
import { ToastContext } from 'contexts';
import { Form } from 'bonfire-kindling';
import { useAuth } from 'providers/AuthProvider';
import SubscriptionFormDesktop from './SubscriptionFormDesktop/SubscriptionFormDesktop';
import useSubscribe from '../hooks/useSubscribe';
import useDiscountCode from '../hooks/useDiscountCode';
import useFetchPlans from '../hooks/useFetchPlans';

const SubscriptionForm = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [subscribeResult, subscribe] = useSubscribe();
  const { activePricingPlans, taxRates } = useFetchPlans();
  const { triggerToast } = useContext(ToastContext);

  // Try to get initial value for email field.
  // Use querystring email param, otherwise current user's email if authenticated.
  const initialEmail = useMemo(() => {
    const emailQueryParam = new URLSearchParams(window.location.search).get('email');

    if (emailQueryParam) {
      return emailQueryParam;
    }

    if (user !== null) {
      return user.email;
    }

    return undefined;
  }, [user]);

  // Used to track our marketing campaigns
  const utmSource = useMemo(
    () => new URLSearchParams(window.location.search).get('utm_source'),
    []
  );

  const annualPrice = useMemo(
    () =>
      activePricingPlans.find(({ recurring = {} }) => recurring.interval === 'year') || {
        id: 'Annual',
        amount: 0,
        currency: 'USD',
      },
    [activePricingPlans]
  );

  const pricingPlansIndexed = useMemo(
    () => activePricingPlans.reduce((result, price) => ({ ...result, [price.id]: price }), {}),
    [activePricingPlans]
  );

  const { discount, validateDiscountCode, isCoupon, isPromotionCode, ...discountResult } =
    useDiscountCode();

  const onSubmit = useCallback(
    values => {
      let errors = {};
      if (values.email && !isEmailValid(values.email)) {
        errors.email = t('subscribe.errors.invalid_email');
      }
      if (values.email !== values.confirmEmail) {
        errors.confirmEmail = t('subscribe.email_match_error');
      }
      ['cardHolderName', 'addressLine1', 'addressCity', 'addressZip'].forEach(key => {
        if (!values[key] || !values[key].trim()) {
          errors[key] = t('subscribe.errors.only_spaces');
        }
      });
      ['addressCountry', 'addressState'].forEach(key => {
        if (!values[key] || !values[key].trim()) {
          errors[key] = t('subscribe.errors.location_required');
        }
      });
      if (Object.keys(errors).length > 0) {
        return errors;
      }
      const price = pricingPlansIndexed[values.selectedPlan] || {};

      const billingInfo = {
        name: values.cardHolderName.trim(),
        addressLine1: values.addressLine1.trim(),
        addressLine2: values.addressLine2 && values.addressLine2.trim(),
        addressCity: values.addressCity.trim(),
        addressState: values.addressState,
        addressZip: values.addressZip.trim(),
        addressCountry: values.addressCountry,
      };

      const subscriptionInfo = {
        name: values.cardHolderName.trim(),
        email: values.email.trim(),
        price,
        discount: { ...discount, isCoupon, isPromotionCode },
        utmSource,
      };
      subscribe({ billingInfo, subscriptionInfo });
    },
    [discount, isCoupon, utmSource, isPromotionCode, pricingPlansIndexed, subscribe, t]
  );

  useEffect(() => {
    if (subscribeResult.isError) {
      return triggerToast({ message: subscribeResult.error.message });
    }
    return undefined;
  }, [subscribeResult.isError, subscribeResult.error, triggerToast]);

  let percentOff = 0;
  if (isCoupon) {
    percentOff = discount.percent_off;
  } else if (isPromotionCode) {
    percentOff = discount.coupon.percent_off;
  }

  const formProps = {
    isSubmitting: subscribeResult.isLoading,
    isLoadingDiscountCode: discountResult.isLoading,
    annualPrice,
    pricingPlansIndexed,
    percentOff,
    taxRates,
    discountCode: isCoupon ? discount.id : discount.code,
    validateDiscountCode,
  };

  return (
    <Form onSubmit={onSubmit} initialValues={{ email: initialEmail, selectedPlan: annualPrice.id }}>
      {({ handleSubmit, values }) => (
        <SubscriptionFormDesktop handleSubmit={handleSubmit} values={values} {...formProps} />
      )}
    </Form>
  );
};
export default SubscriptionForm;
